import type { TranslationDictionary } from "./types";

export const es: TranslationDictionary = {
  // ── EmailGate ──
  "page.title": "Tu Propuesta | Woba",
  "emailGate.placeholder": "tu@correo.com",
  "emailGate.invalidEmail": "Por favor, ingresa un correo válido",
  "emailGate.blockedEmail":
    "Por favor, ingresa un correo corporativo o personal válido",
  "emailGate.loading": "Cargando...",
  "emailGate.notFound": "Propuesta no encontrada",
  "emailGate.loadError":
    "No fue posible cargar la propuesta. Verifica el enlace o intenta de nuevo.",
  "emailGate.retry": "Intentar de Nuevo",
  "emailGate.proposalLoadError":
    "Error al cargar la propuesta. Intenta de nuevo.",

  // ── ProposalCoverScreen (pantalla 1) ──
  "cover.ctaLabel": "Ver mi propuesta",
  "cover.talkToConsultant": "Hablar con {{name}}",
  "cover.scheduleNote.bold": "Propuesta válida hasta {{date}}.",
  "cover.scheduleNote.rest":
    "Asegura tu oficina: agenda una visita presencial o por video.",
  "cover.toggle.traditional": "Alquiler tradicional",
  "cover.toggle.woba": "Woba",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Propuesta no encontrada o expirada.",

  // ── Stepper / navegación por etapas ──
  "steps.comparison": "Ventaja Woba",
  "steps.spaces.singular": "Espacio Seleccionado",
  "steps.spaces.plural": "Espacios Seleccionados",
  "steps.scheduling": "Agendar Visita",
  "steps.proposal": "Hacer Propuesta",
  "steps.customOffice": "Oficina a Medida",
  "stepper.next": "Próximo: {{name}}",
  "nav.back": "Volver",
  "nav.continue": "Continuar",
  "nav.requestVisit": "Solicitar visita",
  "nav.backToRoomsList": "Volver a la lista de oficinas",

  // ── Hero copy (used by ProposalCoverScreen) ──
  "hero.subtitle":
    "Abandona las complejidades del modelo tradicional y centraliza todo en un único contrato inteligente.",
  "hero.emailPrompt":
    "Tu propuesta está lista, ingresa tu correo para acceder.",

  // ── Chat ──
  "chat.contextLabel": "Respondiendo dudas sobre",
  "chat.placeholder": "Pregunta sobre este espacio...",
  "chat.welcomeLine1":
    "¡Hola! 👋 Estoy aquí para resolver tus dudas sobre **{{spaceName}}**.",
  "chat.welcomePrice": "Este espacio cuesta **R$ {{price}}/posición/mes**.",
  "chat.welcomeAskAnything":
    "Pregunta lo que quieras sobre este espacio — si no puedo responder al instante, consultaremos al partner y te avisamos.",
  "chat.errorMessage":
    "Lo sentimos, ocurrió un error al procesar tu mensaje. Intenta de nuevo.",
  "chat.dividerNowAbout": "Ahora sobre: {{spaceName}}",
  "chat.sendAriaLabel": "Enviar",
  "chat.chip.monthlyValue": "¿Cuál es el valor mensual?",
  "chat.chip.parking": "¿Tiene estacionamiento?",
  "chat.chip.internet": "¿Cuál es la velocidad del internet?",
  "chat.chip.meetingRooms": "¿Incluye salas de reunión?",
  "chat.chip.credits": "¿Cómo funcionan los créditos Woba?",
  "chat.chip.contract": "¿Cuál es la flexibilidad del contrato?",
  "chat.chip.scheduleVisit": "Quiero agendar una visita",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Espacios Seleccionados",
  "spaces.optionsFor": "Opciones para {{companyName}}",
  "spaces.optionsForDefault": "Opciones para tu empresa",
  "spaces.bestOfficesInCity":
    "Seleccionamos las mejores oficinas en {{city}} para tu operación",
  "spaces.bestOptions": "Seleccionamos las mejores opciones para tu operación",
  "spaces.availableOptions": "Opciones Disponibles",
  "spaces.recommended": "★ Recomendado",
  "spaces.combo": "Conjunto",
  "spaces.pos": "pos",
  "spaces.scopeOffice": "Oficina Privada",
  "spaces.scopeSpace": "Área común",
  "spaces.detail.roomId": "ID de la sala",
  "spaces.detail.spaceId": "ID del espacio",
  "spaces.detail.back": "Volver",
  "spaces.detail.close": "Cerrar",
  "spaces.detail.expandPhotos": "Ver todas las fotos",
  "spaces.detail.officePhotosHeading": "Fotos de {{label}}",
  "spaces.detail.spacePhotosHeading": "Fotos del Área común",
  "spaces.detail.positionsHeader": "Posiciones",
  "spaces.detail.sizeLabel": "Tamaño",
  "spaces.detail.pricePerPositionLabel": "Val. mensual / posición",
  "spaces.detail.summary":
    "Oficina privada para operar con {{capacity}} posiciones y {{size}} m², en el barrio {{neighborhood}} en {{city}}.",
  "spaces.detail.summaryNoSize":
    "Oficina privada para operar con {{capacity}} posiciones, en el barrio {{neighborhood}} en {{city}}.",
  "spaces.detail.summaryNoNeighborhood":
    "Oficina privada para operar con {{capacity}} posiciones y {{size}} m², en {{city}}.",
  "spaces.detail.summaryNoSizeNoNeighborhood":
    "Oficina privada para operar con {{capacity}} posiciones, en {{city}}.",
  "spaces.detail.summaryComboSuffix":
    " Esta oficina forma parte de un conjunto de {{total}} oficinas reunidas para atender tu solicitud completa.",
  "spaces.detail.spaceSummary":
    "El área común del espacio cuenta con {{amenities}} comodidades disponibles.",
  "spaces.detail.aboutOfficeTitle": "Sobre la oficina",
  "spaces.detail.aboutSpaceTitle": "Sobre el área común",
  // Conformidad (PED-12757): documentos del edificio, nunca de la sala — por
  // eso el tooltip nombra al espacio como sujeto. Solo estados positivos.
  "spaces.detail.complianceTitle": "Conformidad",
  "spaces.detail.complianceAvcb": "Certificado de bomberos (AVCB)",
  "spaces.detail.complianceAvcbInfo":
    "Inspección de bomberos que certifica la seguridad del edificio contra incendios.",
  "spaces.detail.compliancePermit": "Licencia de funcionamiento",
  "spaces.detail.compliancePermitInfo":
    "Licencia municipal que autoriza el funcionamiento del edificio en esta dirección.",
  "spaces.detail.amenitiesTitle": "Amenidades",
  "spaces.detail.amenitiesSubtitleOffice":
    "Qué está disponible dentro de la oficina privada",
  "spaces.detail.amenitiesSubtitleSpace":
    "Qué está disponible en el área común",
  "spaces.detail.locationTitle": "Ubicación",
  "spaces.tabNavLabel": "Navegación del espacio",
  "spaces.fullscreenTitle": "Ubicación de los Espacios",
  "spaces.mapButton": "Mostrar mapa",
  "spaces.map.pinOffices": "{{count}} oficinas",
  "spaces.map.showList": "Mostrar lista",
  "spaces.viewMode.cards": "Ver en tarjetas",
  "spaces.viewMode.list": "Ver en lista",
  "spaces.table.office": "Oficina",
  "spaces.table.monthlyValue": "Valor/mes",
  "spaces.table.pricePerPosition": "Costo/posición",
  "spaces.table.credits": "Pases diarios/mes",
  "spaces.table.creditBonusShort": "Gana {{credits}}",
  "spaces.mapLayers.button": "Puntos de interés",
  "spaces.mapLayers.viewLabel": "Visualización",
  "spaces.mapLayers.view3d": "3D",
  "spaces.mapLayers.view2d": "2D",
  "spaces.mapLayers.poiLabel": "Puntos de interés",
  "spaces.card.sqm": "{{value}} m²",
  "spaces.card.monthlyValue": "Valor mensual",
  "spaces.card.creditBonus": "Gana {{credits}} pases diarios/mes",
  "spaces.card.wantToVisit": "Seleccionar",
  "spaces.card.selected": "Seleccionado",
  "spaces.card.visitRequested": "Visita solicitada",
  "spaces.card.proposalSent": "Propuesta enviada",
  "spaces.card.spaceHasAvcb": "Espacio con AVCB",
  "spaces.card.spaceHasPermit": "Espacio con licencia de funcionamiento",
  "spaces.card.complianceTooltip":
    "El espacio donde está esta sala posee el documento",
  "spaces.card.viewDetails": "Ver detalles",
  "spaces.card.spacePhoto": "Área común",
  "spaces.card.comboRoomLabel": "Oficina {{number}}",
  "spaces.card.comboOfficesCount": "{{count}} oficinas",
  "spaces.card.detailsTitle": "Detalles del espacio",
  "spaces.header.selectToVisit": "Elige el espacio ideal",
  "spaces.header.title.singular": "1 Oficina Privada",
  "spaces.header.title.plural": "{{count}} Oficinas Privadas",
  "spaces.header.title.filteredSingular":
    "{{count}} de {{total}} Oficina Privada",
  "spaces.header.title.filteredPlural":
    "{{count}} de {{total}} Oficinas Privadas",
  "spaces.header.lead":
    "Cada opción pasó por la curaduría de Woba. Ninguna por casualidad.",
  "spaces.header.briefing.singular":
    "Elegimos una Oficina Privada para tu pedido: {{details}}.",
  "spaces.header.briefing.plural":
    "Elegimos algunas Oficinas Privadas para tu pedido: {{details}}.",
  "spaces.header.briefing.inCity": "en {{city}}",
  "spaces.header.briefing.positionsExact": "{{count}} posiciones",
  "spaces.header.briefing.contract": "contrato de {{months}} meses",
  "spaces.filters.sortBy": "Ordenar por",
  "spaces.filters.sortRecommended": "Recomendado",
  "spaces.filters.sortCheapest": "Más barato",
  "spaces.filters.sortMostPositions": "Más posiciones",
  "spaces.filters.capacity": "Capacidad",
  "spaces.filters.priceRange": "Rango de precio",
  "spaces.filters.size": "Tamaño",
  "spaces.filters.capacityUpTo": "Hasta {{max}} posiciones",
  "spaces.filters.capacityBetween": "{{min}} a {{max}} posiciones",
  "spaces.filters.capacityAbove": "Más de {{min}} posiciones",
  "spaces.filters.priceUpTo": "Hasta R$ {{max}}",
  "spaces.filters.priceBetween": "R$ {{min}} a R$ {{max}}",
  "spaces.filters.priceAbove": "Más de R$ {{min}}",
  "spaces.filters.sizeUpTo": "Hasta {{max}} m²",
  "spaces.filters.sizeBetween": "{{min}} a {{max}} m²",
  "spaces.filters.sizeAbove": "Más de {{min}} m²",
  "spaces.filters.noResults": "Ningún espacio coincide con estos filtros.",
  "spaces.selectedBar.hintEmpty": "Selecciona un espacio para continuar",
  "spaces.selectedBar.hintSelected": "Elige cómo deseas continuar",

  // ── PhotosTab ──
  "photos.prevPhoto": "Foto anterior",
  "photos.nextPhoto": "Siguiente foto",
  "photos.noImage": "Sin imagen disponible",
  "photos.didYouLike": "¿Te gustó?",
  "photos.liked": "Me gustó",
  "photos.disliked": "No me gustó",
  "photos.positions": "posiciones",
  "photos.perPosition": "R$ {{price}} / posición",
  "photos.priceOnRequest": "Valor bajo consulta",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee incluido",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Información de amenidades no disponible.",
  "amenities.showOthers": "Otras ({{count}})",
  "amenities.showLess": "Ver menos",

  // ── RoomEmbeddedMap (distance calculator) ──
  "map.calculateDistance": "Calcular distancia entre el espacio y...",
  "map.placeholder": "Ej: Av. Paulista, 1000",
  "map.searching": "Buscando...",
  "map.calculate": "Calcular",
  "map.distancesFrom": "Distancias desde",
  "map.addressNotProvided": "Dirección no informada",
  "map.selectedNoAddress": "Dirección no informada para esta opción",
  "map.recenter": "Centrar",
  "map.recenterTitle": "Centrar el mapa",
  "map.clickToInteract": "Haz clic para interactuar con el mapa",
  "map.locationNotFound":
    "No encontramos esa dirección. Intenta buscar de otra forma.",
  "advantages.goToSlide": "Ir al destacado {{n}}",
  "visitDialog.title": "Agendar visitas",
  "visitDialog.description":
    "En este momento, se enviarían comunicaciones a los Partners seleccionados para verificar la disponibilidad de la visita a sus salas.",
  "poi.noDescription": "Sin descripción",

  // ── ComparisonSection ──
  "comparison.heading":
    "La forma *más inteligente* de contratar y administrar oficinas",

  // ── ComparisonSection (contenido "Ventaja Woba") ──
  "advantage.heading": "Tener una oficina *puede ser fácil*.",
  "advantage.subheadingPre": "Con Woba, tienes acceso a",
  "advantage.subheadingBold": "mucho más que una oficina",
  "advantage.subheadingPost":
    ": todo simple, práctico y sin burocracia. Y sin pagar nada extra por eso.",
  "advantage.cardsAriaLabel": "Ventajas Woba",
  "advantage.card1.title": "Mejor precio garantizado",
  "advantage.card1.descPre": "Con Woba,",
  "advantage.card1.descBold": "paga el mismo valor mensual",
  "advantage.card1.descPost":
    "que negociando directo con el partner, con hasta 15% de descuento negociado — y además llevas toda la red.",
  "advantage.card1.sealAlt": "Sello de mejor precio garantizado",
  "advantage.card1.tagline":
    "¿Encontraste algo mejor, dentro o fuera de la red? Nosotros lo cubrimos.",
  "advantage.card2.title": "Mucho más que una oficina",
  "advantage.card2.stat": "3.000+ espacios · 400+ ciudades",
  "advantage.card2.descPre": "Tu equipo con acceso a",
  "advantage.card2.descBold": "salas de reuniones y pases diarios de coworking",
  "advantage.card2.descPost":
    "en más de 3.000 espacios de la Red Woba en todo Brasil, sin pagar nada extra por eso.",
  "advantage.card2.badge": "Gana horas para usar en la red",
  "advantage.card3.title": "Tu equipo de facilities con superpoderes",
  "advantage.card3.descPre": "Un equipo de",
  "advantage.card3.descBold": "agentes de IA",
  "advantage.card3.descPost":
    "cuida tu operación — accesos, reservas, mantenimiento y contratos — todo resuelto en una sola factura, sin trámites.",
  "advantage.card4.title": "Oficina como servicio",
  "advantage.card4.descBold": "La flexibilidad que necesitas:",
  "advantage.card4.descPost":
    "aumenta, reduce o cambia de ciudad cuando el negocio lo pida. Sin multa, sin CAPEX, sin obra, sin contrato largo.",

  // ── CostBreakdownDrawer (badge "Entiende el precio" en la tarjeta) ──
  "costBreakdown.badge": "Entiende el precio",
  "costBreakdown.badgeWithScope": "Entiende el precio - {{scope}}",
  "costBreakdown.drawerTitle": "Todo eso, en una sola factura.",
  "costBreakdown.drawerSubtitle":
    "Tu equipo se enfoca en el negocio, no en el edificio.",
  "costBreakdown.compositionTitle": "Cómo funciona tu alquiler con Woba",
  "costBreakdown.compositionBodyPre":
    "Esta mensualidad se forma con los mismos 3 grupos de costos de un alquiler tradicional. Aquí, se convierten en un valor único, con una sola fecha de vencimiento, sin hoja de cálculo para conciliar.",
  "costBreakdown.compositionBodyBold": "Todo eso, en una sola factura.",
  "costBreakdown.totalLabel": "Total mensual",
  "costBreakdown.detailTitle": "Cómo funciona un alquiler tradicional",
  "costBreakdown.footnotePre":
    "En un alquiler tradicional, tu factura mensual se divide en 3 grandes grupos. Cada uno de esos grupos tiene varios ítems dentro, lo que suma {{count}} ítems para gestionar: {{count}} proveedores, {{count}} contratos, cada uno con su propia fecha de vencimiento y su propio punto de contacto. Al final, es tu equipo quien carga con ese peso cada mes.",
  "costBreakdown.category.rent": "Alquiler + Condominio + Impuesto predial",
  "costBreakdown.category.services": "Servicios Operativos",
  "costBreakdown.category.support": "Soporte y Depreciación",
  "costBreakdown.item.rentBase": "Alquiler base",
  "costBreakdown.item.condo": "Condominio",
  "costBreakdown.item.iptu": "Impuesto predial",
  "costBreakdown.item.energy": "Energía",
  "costBreakdown.item.staff": "Personal operativo",
  "costBreakdown.item.cleaning": "Limpieza",
  "costBreakdown.item.internet": "Internet / Telecom",
  "costBreakdown.item.maintenance": "Mantenimiento",
  "costBreakdown.item.water": "Agua",
  "costBreakdown.item.furniture": "Mobiliario (depreciación)",
  "costBreakdown.item.amenities": "Café / Amenities",
  "costBreakdown.item.insurance": "Seguros",
  "costBreakdown.item.admin": "Admin / Overhead",

  // ── CTASection / Footer ──
  "cta.tagline": "La forma más inteligente de contratar y administrar oficinas",
  "cta.contact": "Contacto",
  "cta.proposal": "Propuesta",
  "cta.validUntil": "Válida hasta:",
  "cta.validTBD": "Por definir",
  "cta.copyright": "© {{year}} Woba. Todos los derechos reservados.",

  // ── Logos marquee (ProposalCoverScreen footer) ──
  "logos.socialProof": "Haz como miles de empresas que confían en Woba",

  // ── VisitSchedulingStep ──
  "schedule.overline": "Agenda tu visita",
  "schedule.title": "Tú *eliges* cómo quieres conocerlo.",
  "schedule.subtitle":
    "Cuanto más a fondo vayas, más entiendes cómo funciona Woba en la práctica — y cuánto ahorra para {{companyName}}.",
  "schedule.subtitleNoCompany":
    "Cuanto más a fondo vayas, más entiendes cómo funciona Woba en la práctica — y cuánto puede ahorrarte.",
  "schedule.instruction":
    "Elige el día, el horario y el formato de la visita para cada espacio.",
  "schedule.dateTimeLabel": "Día y horario",
  "schedule.previousDates": "Días anteriores",
  "schedule.nextDates": "Próximos días",
  "schedule.modalityLabel": "Formato de la visita",
  "schedule.modalityInPerson": "Presencial",
  "schedule.modalityOnline": "Videollamada",
  "schedule.modalityTestDrive": "Test-drive",
  "schedule.modalityExplainerInPerson":
    "Real y de cerca. Conoce el espacio en persona, revisa la estructura de cerca y siente cómo sería la rutina de tu equipo ahí.",
  "schedule.modalityExplainerOnline":
    "Práctico e inmediato. Agenda una visita por videollamada para conocer la oficina y resolver dudas directo sobre el espacio.",
  "schedule.modalityExplainerTestDrive":
    "Completo, sin prisa. Usa la oficina por un día completo con tu equipo antes de decidir, sin costo.",
  "schedule.testDriveLearnMore": "Saber más",
  "schedule.testDriveDrawerTitle": "Test-drive de 1 día",
  "schedule.testDriveDrawerSubtitle":
    "Completo, sin prisa. Usa la oficina por un día completo con tu equipo antes de decidir, sin costo.*",
  "schedule.testDriveDrawerPhotoAlt":
    "Equipo trabajando en un espacio compartido de Woba",
  "schedule.testDriveHowItWorksTitle": "Cómo funciona",
  "schedule.testDriveStep1":
    "Elige la fecha disponible en el calendario — sin horario fijo, es el día completo.",
  "schedule.testDriveStep2":
    "Woba confirma la disponibilidad directo con el espacio.",
  "schedule.testDriveStep3":
    "Recibes la confirmación y llevas a tu equipo en el día acordado.",
  "schedule.testDriveStep4":
    "Decides con base en la experiencia real, no solo en fotos.",
  "schedule.testDriveBenefitsTitle": "Qué ganas",
  "schedule.testDriveBenefit1":
    "Prueba la rutina de tu equipo en el espacio antes de cerrar.",
  "schedule.testDriveBenefit2": "Sin costo — la visita es por cuenta de Woba.",
  "schedule.testDriveBenefit3":
    "Más seguridad para decidir, con datos reales del día a día.",
  "schedule.testDriveClosingNote":
    "* Sujeto a la confirmación del espacio, dentro de su horario de funcionamiento.",
  "schedule.testDriveNoteLabel": "¿Por qué quieres probar este espacio?",
  "schedule.testDriveNotePlaceholder":
    "Cuéntanos qué te llamó la atención de esta oficina y qué quieres confirmar con el test-drive",
  "schedule.testDriveSingleRoomError":
    "El test-drive es solo para 1 oficina a la vez. Elimina las demás para continuar.",
  "schedule.visitorCountLabel": "¿Cuántas personas van a la visita?",
  "schedule.visitorCountPlaceholder": "Ej: 4 personas",
  "schedule.valueBullet1":
    "Mejor precio garantizado — el mismo que negociando directo.",
  "schedule.valueBullet2": "Gana créditos para salas de reuniones y coworking.",
  "schedule.valueBullet3":
    "Flexibilidad para aumentar, reducir o cambiar de ciudad.",
  "schedule.visitTypeInPerson": "Visita presencial",
  "schedule.visitTypeOnline": "Visita por videollamada",
  "schedule.visitTypeTestDrive": "Test-drive (1 día)",
  "schedule.slotTakenHint": "Este horario ya fue elegido para otro espacio",
  "schedule.fewSlotsAlert":
    "Pocos horarios disponibles. Elige el tuyo y asegura tu visita.",
  "schedule.confirm": "Solicitar visita",
  "schedule.subStepAriaLabel": "Etapas del agendamiento",
  "schedule.subStepWhen": "Día y horario",
  "schedule.subStepData": "Tus datos",
  "schedule.dataOverline": "Ya casi",
  "schedule.dataTitle": "¿Quién va a *participar*?",
  "schedule.dataSubtitle":
    "Completa los datos de quién va a participar de la visita.",
  "schedule.hintIncomplete": "Elige día, horario y formato para cada espacio",
  "schedule.hintContactIncomplete": "Completa los campos para continuar",
  "schedule.fieldEmail": "Correo electrónico",
  "schedule.fieldEmailPlaceholder": "tu@correo.com",
  "schedule.fieldEmailError": "Ingresa un correo electrónico válido",
  "schedule.fieldWhatsappError": "Ingresa un número de teléfono válido",
  "schedule.addParticipant": "Incluir otro participante",
  "schedule.removeVisit": "Quitar este espacio de la visita",
  "schedule.removeParticipant": "Quitar participante",
  "schedule.participantLabel": "Participante {{n}}",
  "schedule.fieldName": "Nombre",
  "schedule.fieldNamePlaceholder": "Tu nombre",
  "schedule.fieldJobTitle": "Cargo",
  "schedule.fieldJobTitlePlaceholder": "Tu cargo",
  "schedule.fieldWhatsapp": "WhatsApp (recomendado)",
  "schedule.fieldWhatsappRequired": "WhatsApp",
  "schedule.fieldWhatsappPlaceholder": "(11) 90000-0000",
  "schedule.fieldCpf": "CPF (documento brasileño)",
  "schedule.fieldCpfPlaceholder": "000.000.000-00",
  "schedule.fieldCpfError": "Ingresa un CPF válido",
  "schedule.fieldCpfHint":
    "Se usa solo para liberar tu acceso al espacio el día del test-drive.",
  "privacy.consentPrefix":
    "Al enviar este formulario, declaras conocer nuestra",
  "privacy.consentLinkLabel": "Política de Privacidad",
  "privacy.consentSuffix":
    ". Tus datos se utilizarán para gestionar la solicitud realizada.",
  "schedule.successTitle": "¡Solicitud enviada!",
  "schedule.successSubtitle":
    "Tu solicitud fue enviada. Atento a tu correo: te avisamos en cuanto el partner confirme el horario.",
  "schedule.fullAddressNotice": "Dirección completa enviada al confirmar.",
  "schedule.postStep0Label": "Solicitud enviada",
  "schedule.postStep0Time": "Completado",
  "schedule.toBeConfirmed": "por confirmar",
  "schedule.postStep1Label": "Visitar oficina",
  "schedule.postStep2Label": "Propuesta final",
  "schedule.postStep3Label": "Firma y ocupa",
  "schedule.postStep1Time": "A partir de 48h",
  "schedule.postStep2Time": "48h después de la visita",
  "schedule.postStep3Time": "Hasta 15 días",
  "schedule.confirmationOverline": "Próximos pasos",
  "schedule.confirmationTitle": "Del interés a la operación, *en pocos días*.",
  "schedule.confirmationSubtitle":
    "Sigue lo que viene hasta que ocupes la nueva oficina.",
  "schedule.recapOverline": "Red Woba",
  "schedule.recapHeading": "Contratas una oficina.\nTe llevas un país entero.",
  "schedule.recapSubheading":
    "El 5% del valor de tu contrato se convierte en bono en créditos, para que tu equipo no quede atado a una única dirección. Puede usar salas de reunión, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil.",
  "schedule.recapPricePre": "¿Y el precio?",
  "schedule.recapPriceBold": "Siempre el mismo que negociando directo",
  "schedule.recapPricePost": "— sin costo extra.",
  "schedule.recapStat1Value": "+3.000",
  "schedule.recapStat1Label": "oficinas flexibles",
  "schedule.recapStat2Value": "+4.200",
  "schedule.recapStat2Label": "salas de reunión",
  "schedule.recapStat3Value": "+1.000",
  "schedule.recapStat3Label": "espacios de eventos",
  "schedule.recapStat4Value": "+400",
  "schedule.recapStat4Label": "ciudades en LatAm",
  "schedule.recapType1Title": "Estaciones de Trabajo",
  "schedule.recapType1DescPre": "1 diaria en cualquiera de las",
  "schedule.recapType1DescCount": "+23.000",
  "schedule.recapType1DescPost": "estaciones",
  "schedule.recapType2Title": "Salas de Reunión",
  "schedule.recapType2DescPre": "1 hora en cualquiera de las",
  "schedule.recapType2DescCount": "+4.200",
  "schedule.recapType2DescPost": "salas",
  "schedule.recapType3Title": "Espacios de Eventos",
  "schedule.recapType3DescPre": "1 diaria en cualquiera de los",
  "schedule.recapType3DescCount": "+1.000",
  "schedule.recapType3DescPost": "espacios",
  "schedule.recapType4Title": "Oficinas Privadas",
  "schedule.recapType4DescPre": "1 mes en cualquiera de las",
  "schedule.recapType4DescCount": "+5.200",
  "schedule.recapType4DescPost": "oficinas",
  "schedule.recapCredits": "créditos",
  "schedule.recapCreditsPerPerson": "créditos/persona",
  "schedule.recapCreditsPerPosition": "créditos/posición",
  "schedule.successNotice":
    "Las condiciones de tu propuesta están garantizadas hasta {{date}}.",

  // ── Spaces (additional) ──
  "spaces.option": "Oficina Privada {{number}}",
  "spaces.ofTotal": "de {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurantes",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Farmacias",
  "poi.transport": "Transporte",
  "poi.hotel": "Hoteles",
  "poi.parking": "Estacionamientos",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "este espacio",

  // ── CreditInfoDrawer ──
  "creditDrawer.viewDetails": "Ver detalles de los créditos",
  "creditDrawer.title": "Créditos incluidos en tu contratación",
  "creditDrawer.subtitle":
    "El 5% del valor de tu contrato se convierte en bono en créditos.",
  "creditDrawer.creditsLabel": "Créditos/mes incluidos*",
  "creditDrawer.creditsValue": "+{{credits}} créditos",
  "creditDrawer.creditsFootnote":
    "* Los créditos no son acumulativos: lo que no se use en el mes no pasa al mes siguiente.",
  "creditDrawer.howItWorksBody":
    "Tu equipo no queda atado a esta dirección: con los créditos incluidos, puede usar salas de reuniones, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil — sin pagar nada extra por eso.",
  "creditDrawer.formatMeetingRoomTitle": "Salas de reuniones",
  "creditDrawer.formatMeetingRoomBody":
    "{{credits}} créditos equivalen a {{hours}}h de sala de reuniones para hasta 4 personas — el valor cambia según el tamaño de la sala.",
  "creditDrawer.formatCoworkingTitle": "Coworking (estaciones compartidas)",
  "creditDrawer.formatCoworkingBody":
    "{{credits}} créditos equivalen a {{credits}} días de uso en espacios compartidos.",
  "creditDrawer.formatPrivateOfficeTitle": "Oficinas privadas por día",
  "creditDrawer.formatPrivateOfficeBody":
    "Usa créditos para armar estructuras temporales para proyectos o equipos.",
  "creditDrawer.formatEventSpaceTitle":
    "Espacios para eventos y capacitaciones",
  "creditDrawer.formatEventSpaceBody":
    "Reserva salas más grandes y auditorios usando varios créditos.",
  "creditDrawer.quadroHoursLabel": "al mes",
  "creditDrawer.quadroDaysLabel": "días/mes",
  "creditDrawer.onRequest": "Bajo consulta",
  "creditDrawer.practiceTitle": "En la práctica",
  "creditDrawer.practiceDayToDayTitle": "Cómo se aplica esto en tu día a día",
  "creditDrawer.practiceBullet1": "Tu oficina deja de ser una única dirección",
  "creditDrawer.practiceBullet2":
    "Eliges el formato correcto para cada momento, sin necesidad de un contrato nuevo",
  "creditDrawer.practiceBullet3":
    "El crédito ya está incluido — no es un costo extra en tu factura",
  "creditDrawer.practiceCta":
    "En lugar de contratar más espacio, usas créditos cuando los necesitas.",

  // ── CreditInfoDrawer, variante "créditos extras" (ENT, PED-12653) ──
  "extraCreditDrawer.viewDetails": "Ver detalles de los créditos extras",
  "extraCreditDrawer.title": "Créditos extras en tu contratación",
  "extraCreditDrawer.subtitle":
    "Un paquete opcional de créditos, además de los incluidos, para usar en la red Woba.",
  "extraCreditDrawer.creditsLabel": "Créditos extras/mes*",
  "extraCreditDrawer.monthlyValueLabel": "Valor del paquete/mes",
  "extraCreditDrawer.howItWorksBody":
    "Además de los créditos incluidos, este paquete le da a tu equipo más créditos por mes para usar salas de reuniones, estaciones de trabajo y espacios de eventos en cualquier ciudad de Brasil — y entra en tu total mensual.",
  "extraCreditDrawer.practiceBullet3":
    "El paquete es opcional y entra en tu total mensual — sin contrato aparte",

  // ── RoomPriceCard (card de precio en el drawer de detalles, desktop) ──
  "priceCard.monthlyLabel": "Mensualidad de la oficina",
  "priceCard.monthlyLabelWithScope": "Mensualidad - {{scope}}",
  "priceCard.perPositionValue": "{{price}} / posición",

  // ── PriceGuaranteeModal ──
  "priceGuarantee.title": "Garantía de mejor precio",
  "priceGuarantee.subtext":
    "Mismo precio que el partner, hasta 15% de descuento",
  "priceGuarantee.bodyPre":
    "Negociamos directamente con el partner para garantizar el mejor precio de tu propuesta. Conseguimos ",
  "priceGuarantee.bodyBold": "hasta 15% de descuento",
  "priceGuarantee.bodyPost": " sobre el valor de la oficina.",
  "priceCard.creditsLabel": "Créditos incluidos",
  "priceCard.freeBadge": "gratis",
  "priceCard.creditsSubtext":
    "Equivalente a {{credits}} pases diarios en espacios compartidos/mes",
  "priceCard.flexCreditsLabel": "Créditos extras (opcional)",
  "priceCard.setupFeeLabel": "Setup Fee",
  "priceCard.setupFeeCaption": "Pagado una única vez",
  "priceCard.licenseFeeLabel": "License Fee",
  "priceCard.licenseFeeSemiannualCaption": "Pagado semestralmente",
  "priceCard.licenseFeeAnnualCaption": "Pagado anualmente",
  "priceCard.helpLink": "¿Todavía tienes dudas? Habla con nosotros",
  "priceCard.scheduleVisit": "Solicitar visita",
  "priceCard.makeProposal": "Hacer propuesta",

  // ── ProposalMakingStep (flujo "Hacer propuesta") ──
  "proposalMaking.hub.overline": "Haz tu propuesta",
  "proposalMaking.hub.headline": "Tú *decides* cómo avanzar.",
  "proposalMaking.hub.subtitle":
    "Revisa los valores y el resumen de la sala antes de elegir los próximos pasos para {{companyName}}.",
  "proposalMaking.hub.subtitleNoCompany":
    "Revisa los valores y el resumen de la sala antes de elegir los próximos pasos.",
  "proposalMaking.subStepDefine": "Condiciones de la propuesta",
  "proposalMaking.subStepAdjust": "Ajuste de precio y del espacio",
  "proposalMaking.subStepReview": "Identificación",
  "proposalMaking.subStepAriaLabel": "Pasos de la propuesta",
  "proposalMaking.choose.pickerLabel": "Elige una opción",
  "proposalMaking.choose.acceptLabel": "Aceptar condiciones",
  "proposalMaking.choose.acceptDescription":
    "Aceptar las condiciones actuales agiliza el análisis y pone tu propuesta por delante de las que aún se están negociando.",
  "proposalMaking.choose.negotiateLabel": "Negociar valor o pedir un ajuste",
  "proposalMaking.choose.negotiateDescription":
    "Propón un valor diferente o pide ajustes en el espacio — nuestro equipo evalúa y responde en hasta 2 días hábiles.",
  "proposalMaking.negotiate.overline": "Negociar valor",
  "proposalMaking.negotiate.headline": "Propón el valor *ideal* para ti.",
  "proposalMaking.negotiate.subtitle":
    "Ajusta el valor mensual propuesto y cuéntale a nuestro equipo por qué tiene sentido para ti — cuanto más cerca del valor actual, mayor la probabilidad de una aceptación rápida por parte del socio.",
  "proposalMaking.negotiate.subtitleNoPrice":
    "Cuéntale a nuestro equipo qué te gustaría proponer y por qué tiene sentido para ti — lo evaluará junto con el socio y te responderá.",
  "proposalMaking.negotiate.sectionTitle": "Ajuste de precio",
  "proposalMaking.negotiate.fieldLabel": "Tu propuesta",
  "proposalMaking.negotiate.fieldPlaceholder": "0",
  "proposalMaking.negotiate.goodProposalTitle": "¡Excelente propuesta!",
  "proposalMaking.negotiate.goodProposalDescription":
    "Un valor igual o mayor al actual tiene alta probabilidad de ser aceptado.",
  "proposalMaking.negotiate.moderateDiscountTitle":
    "Buena probabilidad de aceptación",
  "proposalMaking.negotiate.moderateDiscountDescription":
    "Descuentos de hasta el 15% tienen buenas probabilidades de ser aprobados por el socio.",
  "proposalMaking.negotiate.overLimitTitle": "Descuento por encima del límite",
  "proposalMaking.negotiate.overLimitDescription":
    "Podemos enviar propuestas con hasta {{percent}}% de descuento. Ajusta a {{minimum}} o más para continuar.",
  "proposalMaking.negotiate.overLimitHint":
    "Ajusta la propuesta a {{minimum}} o más para continuar",
  "proposalMaking.negotiate.discountBadge": "-{{percent}}%",
  "proposalMaking.negotiate.increaseBadge": "+{{percent}}%",
  "proposalMaking.negotiate.reasonLabel": "Cuéntanos el motivo de tu propuesta",
  "proposalMaking.negotiate.reasonPlaceholder":
    "Cuéntanos el motivo del ajuste de valor...",
  "proposalMaking.negotiate.flexCreditsLabel": "Créditos extras por mes",
  "proposalMaking.negotiate.flexCreditsUnit": "créditos",
  "proposalMaking.negotiate.flexCreditsHelper":
    "Equivale a {{value}}/mes. La negociación de arriba aplica solo a la mensualidad.",
  "proposalMaking.adjustmentsChoice.pickerLabel":
    "Ajuste de configuración del espacio",
  "proposalMaking.adjustmentsChoice.overline": "Ajustes en el espacio",
  "proposalMaking.adjustmentsChoice.headline": "¿Necesitas algún *ajuste*?",
  "proposalMaking.adjustmentsChoice.subtitle":
    "Puedes pedir retirar, instalar, reparar o sustituir un artículo.",
  "proposalMaking.adjustmentsChoice.noneLabel": "Sin ajustes",
  "proposalMaking.adjustmentsChoice.noneBadge":
    "Mayor probabilidad de ser aceptada",
  "proposalMaking.adjustmentsChoice.someLabel": "Con ajustes",
  "proposalMaking.adjustmentsChoice.someBadge":
    "Sujeto a evaluación del espacio",
  "proposalMaking.adjustmentType.overline": "Tipo de ajuste",
  "proposalMaking.adjustmentType.headline": "¿Cuál es el tipo de *ajuste*?",
  "proposalMaking.adjustmentType.subtitle":
    "Puedes agregar un ajuste a la vez.",
  "proposalMaking.adjustmentType.layout": "Layout",
  "proposalMaking.adjustmentType.layoutDescription":
    "Retirar o instalar un artículo, o cambiar/sustituir algo en el espacio.",
  "proposalMaking.adjustmentType.maintenance": "Mantenimiento o reparación",
  "proposalMaking.adjustmentType.maintenanceDescription":
    "Reparar o dar mantenimiento a algo que ya existe.",
  "proposalMaking.adjustmentType.positions": "Posiciones",
  "proposalMaking.adjustmentType.positionsDescription":
    "Aumentar o reducir el número de posiciones de la oficina.",
  "proposalMaking.adjustmentDescription.overline": "Detalle del ajuste",
  "proposalMaking.adjustmentDescription.headline":
    "Describe lo que *necesitas*.",
  "proposalMaking.adjustmentDescription.subtitle":
    "Cuantos más detalles, más rápido evaluamos tu pedido.",
  "proposalMaking.adjustmentDescription.placeholder":
    "Describe lo que necesitas...",
  "proposalMaking.adjustmentDescription.continueLabel": "Revisar",
  "proposalMaking.review.overline": "Ya casi",
  "proposalMaking.review.headline": "Revisa tu *propuesta*.",
  "proposalMaking.review.subtitle":
    "Confirma los valores y ajustes antes de enviarla a nuestro equipo.",
  "proposalMaking.review.bannerAccepted": "¡Esta es una excelente propuesta!",
  "proposalMaking.review.bannerNegotiating":
    "Tu propuesta será enviada para revisión de nuestro socio.",
  "proposalMaking.review.adjustmentsTitle": "Ajustes solicitados",
  "proposalMaking.review.addAdjustment": "Agregar ajuste",
  "proposalMaking.review.removeAdjustment": "Eliminar",
  "proposalMaking.review.identificationTitle": "Tus datos",
  "proposalMaking.review.occupancyDateLabel": "Fecha prevista de ocupación",
  "proposalMaking.review.contractTermLabel": "Plazo del contrato",
  "proposalMaking.review.contractTermOption.12": "12 meses",
  "proposalMaking.review.contractTermOption.24": "24 meses",
  "proposalMaking.review.contractTermOption.36": "36 meses",
  "proposalMaking.review.contractTermOption.48": "48 meses",
  "proposalMaking.review.contractTermOption.other": "Otro",
  "proposalMaking.review.contractTermCustomPlaceholder": "¿Cuántos meses?",
  "proposalMaking.review.submitLabel": "Confirmar y enviar",
  "proposalMaking.sent.overline": "Próximos pasos",
  "proposalMaking.sent.headline": "¡Propuesta enviada!",
  "proposalMaking.sent.subtitle": "Tu propuesta fue enviada al equipo Woba.",
  "proposalMaking.sent.notice": "Esta propuesta es válida hasta {{date}}.",
  "proposalMaking.sent.roomStatusLabel": "Propuesta enviada",
  "proposalMaking.sent.postStep0Label": "Propuesta enviada",
  "proposalMaking.sent.postStep0Time": "Completado",
  "proposalMaking.sent.postStep1Label": "En análisis",
  "proposalMaking.sent.postStep1Time": "Respuesta en hasta 2 días hábiles",
  "proposalMaking.sent.postStep2Label": "Aprobación",
  "proposalMaking.sent.postStep2Time": "Tras la respuesta del socio",
  "proposalMaking.sent.postStep3Label": "Firma y ocupa",
  "proposalMaking.sent.postStep3Time": "Hasta 15 días",

  // ── Card "A Medida" (PED-13464) + flujo de briefing ──
  "customOffice.card.title": "¿Prefieres una oficina a medida para tu empresa?",
  "customOffice.card.subtitle":
    "Podemos ir más allá de estas opciones. Si quieres algo más exclusivo, montamos una oficina a medida, sin que tengas que lidiar con inmobiliaria, obra y proveedores.",
  "customOffice.card.bullet1": "Encontramos el espacio ideal",
  "customOffice.card.bullet2": "Proyecto 100% personalizado",
  "customOffice.card.bullet3": "Operación completa incluida",
  "customOffice.card.bullet4": "Todos los beneficios Woba",
  "customOffice.card.cta": "Armar mi oficina",
  // Califica antes del clic (temor del equipo: cliente entrando como si
  // fuera una oficina lista más): proyecto exclusivo, inversión mayor.
  "customOffice.card.footnote":
    "Proyecto exclusivo · Inversión por encima de una oficina lista",
  // Flujo de briefing — las 5 preguntas de la referencia "Briefing rápido ·
  // Camada 1", reescritas para un formulario que el cliente
  // responde solo dentro de la propuesta: sin jerga interna, sin
  // abreviaturas, cada campo se explica.
  "customOffice.hub.overline": "Oficina a medida · 3 etapas",
  "customOffice.hub.headline": "Vamos a diseñar el *lugar* de tu equipo",
  "customOffice.hub.subtitle":
    "Responde cinco preguntas rápidas sobre lo que tu equipo necesita y deja tus datos de contacto. Toma unos 10 minutos y ya es suficiente para que Woba empiece a buscar el espacio ideal para ti.",
  "customOffice.hub.noticeTitle": "Antes de empezar",
  "customOffice.hub.noticeBody":
    "La oficina a medida es un proyecto exclusivo: encontramos el inmueble, diseñamos la oficina para tu equipo y nos encargamos de la operación. Por eso, la inversión suele quedar por encima de una oficina lista, y la entrada toma más tiempo, porque puede haber obra. Si una oficina lista te sirve, sigue siendo el camino más rápido y económico.",
  "customOffice.hub.previewTitle": "Lo que vas a responder",
  "customOffice.hub.footnote":
    "Después de enviar, nuestro equipo se pone en contacto para afinar los detalles.",
  "customOffice.stepper.ariaLabel": "Etapas del formulario",
  // 3 etapas: el espacio (preguntas 01 y 02), equipo y prioridades (03 a
  // 05) y "Tus datos" solo — cada pregunta sigue siendo su propia tarjeta.
  "customOffice.stepper.place": "El espacio",
  "customOffice.stepper.routine": "Equipo y prioridades",
  "customOffice.stepper.contact": "Tus datos",
  "customOffice.stage.overline": "Etapa {{number}} de {{total}}",
  "customOffice.stage.place.title": "¿Cómo es la oficina ideal?",
  "customOffice.stage.place.subtitle":
    "Dónde queda, para cuántas personas y qué tipo de inmueble buscas.",
  "customOffice.stage.routine.title": "¿Cómo va a usar el espacio tu equipo?",
  "customOffice.stage.routine.subtitle":
    "El día a día, lo que no puede faltar y referencias que ayudan a acertar.",
  "customOffice.stage.contact.title": "Tus datos",
  "customOffice.stage.contact.subtitle":
    "A quién debemos responder con la propuesta a medida.",
  "customOffice.q1.title": "¿Dónde y para cuántos?",
  "customOffice.q1.subtitle":
    "Dónde debe quedar la oficina, para cuántas personas y para cuándo.",
  "customOffice.q1.regionLabel": "Ciudad y región / barrio deseado",
  "customOffice.q1.regionPlaceholder": "Ej.: São Paulo, Pinheiros o Faria Lima",
  "customOffice.q1.peopleLabel": "Número de personas",
  "customOffice.q1.peoplePlaceholder": "Ej.: 20",
  "customOffice.q1.sizeLabel": "Metraje aproximado (m²)",
  "customOffice.q1.sizePlaceholder": "Si lo sabes. Ej.: 200",
  "customOffice.q1.deadlineLabel": "Plazo para entrar",
  "customOffice.q1.deadlinePlaceholder": "Ej.: en 60 días, hasta marzo",
  "customOffice.requiredHint":
    "Completa los campos obligatorios para continuar",
  "customOffice.q2.title": "¿Cómo es el lugar ideal?",
  "customOffice.q2.subtitle":
    "El estándar del edificio, el tipo de inmueble y si necesitas plazas de estacionamiento.",
  "customOffice.q2.standardLabel": "Estándar deseado",
  "customOffice.q2.standardOption.premium": "Alto estándar (AAA)",
  "customOffice.q2.standardOption.intermediate": "Intermedio",
  "customOffice.q2.standardOption.simple": "Más sencillo",
  "customOffice.q2.buildingTypeLabel": "Tipo de inmueble",
  "customOffice.q2.buildingTypeOption.corporateSlab":
    "Planta en edificio corporativo",
  "customOffice.q2.buildingTypeOption.streetBuilding":
    "Casa o inmueble a la calle",
  "customOffice.q2.buildingTypeOption.either": "Da igual",
  "customOffice.q2.parkingLabel": "Estacionamiento",
  "customOffice.q2.parkingPlaceholder": "¿Cuántas plazas necesitas? Ej.: 5",
  "customOffice.q2.readinessLabel":
    "¿Prefieres una oficina lista o aceptas hacer obra?",
  "customOffice.q2.readinessOption.readyToUse": "Lista para usar",
  "customOffice.q2.readinessOption.openToCustomBuild":
    "Acepto una obra a medida",
  "customOffice.q3.title": "¿Cómo es el día a día del equipo?",
  "customOffice.q3.subtitle": "Cuántas salas de reunión necesita el equipo.",
  "customOffice.q3.meetingSmallLabel": "Pequeñas (hasta 4 personas)",
  "customOffice.q3.meetingSmallPlaceholder": "¿Cuántas?",
  "customOffice.q3.meetingMediumLabel": "Medianas (6 a 8 personas)",
  "customOffice.q3.meetingMediumPlaceholder": "¿Cuántas?",
  "customOffice.q3.meetingLargeLabel": "Grandes (10 o más)",
  "customOffice.q3.meetingLargePlaceholder": "¿Cuántas?",
  "customOffice.q3.meetingRoomsLabel": "Salas de reunión",
  "customOffice.q4.title": "¿Qué es prioridad?",
  "customOffice.q4.subtitle":
    "Las comodidades básicas, lo que no puede faltar de ninguna manera y lo que sería bueno tener.",
  // Las comodidades vinieron de la pregunta 03 (es una
  // elección de prioridad, no rutina). Los ejemplos de "no puede faltar"
  // deben ser cosas que los chips no cubren, o el cliente repite la lista.
  "customOffice.q4.amenitiesLabel": "Comodidades básicas obligatorias",
  "customOffice.q4.amenitiesOption.kitchen": "Cocina o comedor",
  "customOffice.q4.amenitiesOption.buildingReception":
    "Recepción en el edificio",
  "customOffice.q4.amenitiesOption.publicTransport": "Transporte público cerca",
  "customOffice.q4.amenitiesOption.restaurants": "Restaurantes cerca",
  "customOffice.q4.nonNegotiableLabel": "Qué más no puede faltar",
  "customOffice.q4.nonNegotiableHelp":
    "Indica un ítem por línea, empezando por el más importante. Ejemplos: aceptar mascotas, certificación ambiental del edificio, área de carga y descarga, sala segura, recepción 24 horas.",
  "customOffice.q4.priority1Label": "1º",
  "customOffice.q4.priority2Label": "2º",
  "customOffice.q4.priority3Label": "3º",
  "customOffice.q4.niceToHaveLabel": "Sería bueno tener",
  "customOffice.q4.niceToHaveHelp":
    "Opcional. Cosas que marcarían la diferencia, pero no deciden la elección.",
  "customOffice.q4.niceToHavePlaceholder":
    "Ej.: bicicletero, gimnasio en el edificio, terraza",
  "customOffice.q5.title": "¿Ya viste algún inmueble que te gustó?",
  "customOffice.q5.subtitle":
    "Ejemplos de lo que te gustó o descartaste ayudan a acertar más rápido.",
  "customOffice.q5.likedLabel":
    "¿Alguna oficina o edificio que viste y te gustó? Cuéntanos qué pesó: dirección, edificio, planta, precio.",
  "customOffice.q5.likedPlaceholder":
    "Ej.: el edificio X en Faria Lima, por la ubicación y la planta",
  "customOffice.q5.discardedLabel": "¿Alguno que descartaste? ¿Por qué?",
  "customOffice.q5.discardedPlaceholder":
    "Ej.: el edificio Y, porque no tenía estacionamiento",
  // Última etapa: los mismos 4 campos de identificación de la propuesta
  // ("Tus datos" en la revisión), reutilizando el ContactForm y sus rótulos.
  "customOffice.contact.hint": "Completa tus datos para enviar",
  "customOffice.submitLabel": "Enviar respuestas",
  "customOffice.sent.overline": "Próximos pasos",
  // Pantalla final igual a la de visita/propuesta: stepper de lo que viene,
  // tarjeta resumen de lo pedido y la propuesta de valor de la red abajo.
  "customOffice.sent.summaryLabel": "Respuestas enviadas",
  "customOffice.sent.postStep0Label": "Respuestas enviadas",
  "customOffice.sent.postStep0Time": "Completado",
  "customOffice.sent.postStep1Label": "Búsqueda de inmuebles",
  "customOffice.sent.postStep1Time": "En hasta 7 días",
  "customOffice.sent.postStep2Label": "Propuesta a medida",
  "customOffice.sent.postStep2Time": "Después de la búsqueda",
  "customOffice.sent.postStep3Label": "Firma y ocupa",
  "customOffice.sent.postStep3Time": "Plazo a acordar",
  "customOffice.summary.likedLabel": "Te gustó",
  "customOffice.summary.discardedLabel": "Descartaste",
  "customOffice.sent.headline": "¡Respuestas enviadas!",
  "customOffice.sent.subtitle":
    "Nuestro equipo usará tus respuestas para armar una propuesta a medida. La recibes en hasta 7 días.",

  // ── Rating (evaluación de la recomendación) ──
  "rating.prompt": "¿Acertamos en la elección?",
  "rating.drawerPrompt": "¿Acertamos en la elección de esta oficina?",
  "rating.thumbUp": "Me gustó esta oficina",
  "rating.thumbDown": "No me gustó esta oficina",
  "rating.modalTitlePositive": "¡Bien! Acertamos en la recomendación",
  "rating.modalTitleNegative": "Todavía no acertamos en la recomendación",
  "rating.modalSupportPositive":
    "Tu evaluación nos ayuda a entender qué te funcionó.",
  "rating.modalSupportNegative":
    "Tenemos más opciones para ti. Tu evaluación nos ayuda a traer las más acertadas.",
  "rating.questionNegative": "¿Qué no encajó?",
  "rating.questionPositive": "¿Qué fue lo que más te gustó?",
  "rating.subReasonsLabel": "¿Qué falta?",
  "rating.commentLabel": "¿Quieres contarnos más? (opcional)",
  "rating.commentPlaceholder": "Escribe aquí...",
  "rating.submit": "Enviar evaluación",
  "rating.confirmationTitle": "¡Recibido!",
  "rating.confirmationBody":
    "Usaremos tu evaluación para mejorar las próximas sugerencias.",
  "rating.close": "Cerrar",
  "rating.reason.photos": "Las fotos no ayudan a entender la oficina",
  "rating.reason.capacity":
    "No tiene capacidad para el número de personas que necesito",
  "rating.reason.location": "La zona no me funciona",
  "rating.reason.price": "No vale el precio pedido",
  "rating.reason.layoutCondition":
    "El layout o el estado de la oficina no me agradó",
  "rating.reason.missingRequirements": "Falta algo que necesito",
  "rating.reason.capacityFit": "Le queda bien a mi equipo",
  "rating.reason.locationGreat": "La zona es excelente para mí",
  "rating.reason.visualAppeal": "Me gustó el aspecto de la oficina",
  "rating.reason.amenitiesComplete": "Tiene todo lo que necesito",
  "rating.reason.priceValue": "Buena relación calidad-precio",
  "rating.subReason.amenities": "Amenidades",
  "rating.subReason.equipment": "Equipamiento",
  "rating.subReason.accessibility": "Accesibilidad",
  "rating.subReason.other": "Otras características",
};
